import { FC, useEffect, useRef, useState } from 'react';
import { DonationTotal } from '../DonationAmount/DonationTotal';
import { IPage } from 'store/donatePage/types';
import { CandidateType } from '../Candidates/Candidates';
import { OathRecurringInterval } from 'store/pay/types';
import { formatNumberAsCurrency, getShowAllDonateMethods, getUrlParams } from 'helpers/utils';
import { trackPixel } from 'helpers/pixel';
import { ENV_CONFIG } from 'config/environment';
import { CustomSplit } from '../CustomSplit';
import { CustomLinkCopy } from '../CustomSplit/CustomLinkCopy';
import { ADMINS_ONLY, getIsFeatureOn } from 'helpers/featureFlags';
import {
  getActionVerb,
  getSelectedCandidates,
  getSelectedCandidatesWithValidAmount,
} from 'helpers/donate';
import classNames from 'classnames';
import { PledgeDonateButtons } from './PledgeDonateButtons';
import { PaymentLogos } from './PaymentLogos';
import { BREAKPOINTS } from 'constants/breakpoints';
import { SCROLL_TARGETS } from 'constants/scrollTargets';
import { ProcessingRedirectOptions } from 'store/donatePage/constants';

interface Props {
  page?: IPage;
  isDafEnabled: boolean;
  candidates: CandidateType[];
  selectedDonationAmount?: number;
  overageMax?: number;
  onDonateClick: (selectedMethod?: string, selectedMethodType?: string) => void;
  onUserDonationTotalChange: (value?: number) => void;
  setCandidates: React.Dispatch<React.SetStateAction<CandidateType[]>>;
  setSelectedDonationAmount: React.Dispatch<React.SetStateAction<number | undefined>>;
  showCustomSplit: boolean;
  onDafDonateClick: () => void;
  isRecurring: boolean;
  setIsRecurring: (isRecurring: boolean) => void;
  recurringInterval?: OathRecurringInterval;
  isMonthlyEnabled: boolean;
  containerClassName?: string;
  enableScrollToDonate?: boolean;
}

export const AmountDetailsContent: FC<Props> = ({
  page,
  candidates,
  isDafEnabled,
  onDonateClick,
  onUserDonationTotalChange,
  selectedDonationAmount,
  overageMax,
  setCandidates,
  setSelectedDonationAmount,
  showCustomSplit,
  onDafDonateClick,
  isRecurring,
  setIsRecurring,
  isMonthlyEnabled,
  recurringInterval = 'month',
  containerClassName,
  enableScrollToDonate = false,
}) => {
  const { urlDonateMethod, urlPartner, urlScroll } = getUrlParams();
  const [donateMethod, setDonateMethod] = useState<string | undefined>(urlDonateMethod);
  const [showAllDonateMethods, setShowAllDonateMethods] = useState(
    getShowAllDonateMethods(urlDonateMethod)
  );
  const [isRecurringDonationValid, setIsRecurringDonationValid] = useState(false);
  const donateButtonRef = useRef<HTMLButtonElement>(null);
  const hasScrolledToDonate = useRef(false);

  const getOnDonateButtonClick = (selectedMethod?: string, selectedMethodType?: string) => () => {
    trackPixel('donation');
    onDonateClick(selectedMethod, selectedMethodType);
  };
  const onDonateButtonClick = getOnDonateButtonClick();
  const isWireEnabled = page?.wire_enabled;
  const isStripePage = page?.processing_redirect === ProcessingRedirectOptions.STRIPE;
  const showCardOrAchButton = Boolean(
    showAllDonateMethods || !isDafEnabled || donateMethod === 'card' || donateMethod === 'ach'
  );

  useEffect(() => {
    const newDonateMethod = isRecurring ? 'card' : urlDonateMethod;
    setDonateMethod(newDonateMethod);
    setShowAllDonateMethods(getShowAllDonateMethods(newDonateMethod));
  }, [isRecurring, urlDonateMethod]);

  useEffect(() => {
    const evenSplitAmount = ((selectedDonationAmount || 0) / candidates.length) * 100;
    const evenSplitRounded = {
      up: Math.ceil(evenSplitAmount),
      down: Math.floor(evenSplitAmount),
    };
    const allCandidatesSelected = getSelectedCandidates(candidates).length === candidates.length;
    const isSplitCustom = candidates.some(
      candidate =>
        Math.ceil((candidate.donationAmount || 0) * 100) !== evenSplitRounded.up &&
        Math.floor((candidate.donationAmount || 0) * 100) !== evenSplitRounded.down
    );
    setIsRecurringDonationValid(allCandidatesSelected && !isSplitCustom);
  }, [candidates, selectedDonationAmount]);

  useEffect(() => {
    const isMobile = window.innerWidth <= BREAKPOINTS.TABLET;
    if (
      !enableScrollToDonate ||
      hasScrolledToDonate.current ||
      !isMobile ||
      urlScroll !== SCROLL_TARGETS.DONATE
    )
      return;

    if (!donateButtonRef.current) return;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    hasScrolledToDonate.current = true;

    donateButtonRef.current.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'center',
    });
  }, [urlScroll, showCardOrAchButton, enableScrollToDonate]);

  return (
    <DonationTotal
      containerClassName={containerClassName}
      donationAmounts={page?.donation_amounts || []}
      isMonthlyEnabled={isMonthlyEnabled}
      isRecurring={isRecurring}
      isRecurringDonationValid={isRecurringDonationValid}
      numRecipients={candidates.length}
      clientId={page?.client_id}
      selectedDonationAmount={selectedDonationAmount}
      setIsRecurring={setIsRecurring}
      recurringInterval={recurringInterval}
      setSelectedDonationAmount={onUserDonationTotalChange}
    >
      <>
        {(page?.allocation_type === 'WATERFALL' || page?.allocation_type === 'WEIGHTED') &&
          selectedDonationAmount &&
          getSelectedCandidatesWithValidAmount(candidates).length > 0 && (
            <div className="display-flex body-small-text justify-around mb10 mt8">
              <div>
                {`Your ${formatNumberAsCurrency(
                  selectedDonationAmount || 0
                )} contribution will be split amongst `}
                <span className="semi-bold">{`${
                  getSelectedCandidatesWithValidAmount(candidates).length
                } recipients`}</span>
              </div>
            </div>
          )}
        {showCardOrAchButton && (
          <>
            <div className="display-flex justify-around">
              <button
                type="button"
                ref={donateButtonRef}
                className={classNames('button-brand', 'donate-button', 'split-screen', {
                  'bg-dark-gray': isRecurring && !isRecurringDonationValid,
                })}
                onClick={onDonateButtonClick}
                disabled={isRecurring && !isRecurringDonationValid}
              >
                {urlPartner !== 'f4d-nvi'
                  ? `${getActionVerb(page).toUpperCase()} BY CARD / ACH`
                  : 'CREDIT CARD / ACH'}
              </button>
            </div>
            {isStripePage && <PaymentLogos />}
          </>
        )}
        <PledgeDonateButtons
          isWireEnabled={isWireEnabled}
          isRecurring={isRecurring}
          showAllDonateMethods={showAllDonateMethods}
          donateMethod={donateMethod}
          page={page}
          getOnDonateButtonClick={getOnDonateButtonClick}
        />
        {isDafEnabled && !isRecurring && (showAllDonateMethods || donateMethod === 'daf') && (
          <div className="display-flex justify-around">
            <button
              type="button"
              className="button-brand donate-button button-brand-daf split-screen"
              onClick={onDafDonateClick}
            >
              Continue with{' '}
              <img
                src={`${ENV_CONFIG().CLOUD_STORAGE.URL}/static_content/daf_logo.png`}
                alt="DAF Logo"
              />
            </button>
          </div>
        )}
        {!isRecurring || !isRecurringDonationValid ? (
          <CustomSplit
            allocationType={page?.allocation_type}
            candidates={candidates}
            donationTotal={selectedDonationAmount}
            overageMax={overageMax}
            recipientGroups={page?.recipient_groups}
            resetDonationTotal={onUserDonationTotalChange}
            setCandidates={setCandidates}
            setDonationTotal={setSelectedDonationAmount}
            showCustomSplit={showCustomSplit || (isRecurring && !isRecurringDonationValid)}
          />
        ) : (
          getIsFeatureOn(ADMINS_ONLY) &&
          getSelectedCandidates(candidates).length >= 1 && (
            <div className="donation-total-link-container">
              <CustomLinkCopy candidates={candidates} isRecurring={isRecurring} />
            </div>
          )
        )}
      </>
    </DonationTotal>
  );
};
